/**
 * CRM AI テキスト解析用の API hook
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useState } from "react";

/**
 * タレント用 AI 解析レスポンスの型
 */
export type CrmParseTalentResult = {
  name: string | null;
  nameKana: string | null;
  email: string | null;
  phone: string | null;
  affiliation: string | null;
  employmentType: string | null;
  partnerCompany: string | null;
  nearestStation: string | null;
  remotePreference: string | null;
  age: number | null;
  desiredReward: number | null;
  availableFrom: string | null;
  notes: string | null;
};

/**
 * 案件用 AI 解析レスポンスの型
 */
export type CrmParseMatterResult = {
  title: string | null;
  clientCompany: string | null;
  rewardMin: number | null;
  rewardMax: number | null;
  nearestStation: string | null;
  remoteType: string | null;
  deadline: string | null;
  requiredSkills: string | null;
  niceToHaveSkills: string | null;
  notes: string | null;
};

/**
 * CRM フリーテキストを AI で解析する hook
 * @return {object} parseTalentText, parseMatterText, loading, error
 */
export function useCrmParseText(): {
  parseTalentText: (text: string) => Promise<CrmParseTalentResult>;
  parseMatterText: (text: string) => Promise<CrmParseMatterResult>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const parseTalentText = useCallback(
    async (text: string): Promise<CrmParseTalentResult> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<CrmParseTalentResult>(
          "/crm/parse-text",
          { text, entityType: "talent" },
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const parseMatterText = useCallback(
    async (text: string): Promise<CrmParseMatterResult> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<CrmParseMatterResult>(
          "/crm/parse-text",
          { text, entityType: "matter" },
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { parseTalentText, parseMatterText, loading, error };
}
